import { goSolidApi } from "@/composables/useGoSolidApi";
import {
  isItemAvailable,
  isItemWaitingForSale,
} from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import type { FlattenedPortfolioItem } from "@/types";
import { defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";

/**
 * Shared cache of flattened portfolio items for the active company. Holds
 * no selection state — callers that show a subset pass it to the table as
 * `items` —
 * see {@link usePortfolioSelection} for per-instance selection.
 *
 * `allItems` is a `shallowRef` to avoid making 10k+ items deeply reactive.
 * Consumers fetch via {@link fetchItems} or {@link refetchItems}; views
 * that own custom fetching can register their loader via
 * {@link registerRefetch} so refetches stay consistent. Only the latest
 * request's answer is kept, so a slow response for a company the user has
 * since left never replaces the current one.
 */
export const usePortfolioItemsStore = defineStore("portfolioItemsStore", () => {
  // shallowRef to avoid making 10,000+ objects reactive (performance)
  const allItems = shallowRef<FlattenedPortfolioItem[]>([]);
  const isLoading = ref(false);
  const currentCompanyId = ref<number | null>(null);
  const refetchAction = shallowRef<(() => Promise<void>) | null>(null);
  const sourceHasMultipleCompanies = ref(false);
  let latestRequest = 0;

  const itemsMap = computed(() => {
    const map = new Map<number, FlattenedPortfolioItem>();
    for (const item of allItems.value) {
      map.set(item.id, item);
    }
    return map;
  });

  const availableItems = computed((): FlattenedPortfolioItem[] => {
    return allItems.value.filter(isItemAvailable);
  });

  const totalAvailableGOs = computed((): number => {
    const availableForSale = allItems.value.filter(
      (item) => isItemAvailable(item) || isItemWaitingForSale(item)
    );
    return availableForSale.reduce((total, item) => total + item.count, 0);
  });

  function setAllItems(items: FlattenedPortfolioItem[]): void {
    allItems.value = items;
  }

  function registerRefetch(action: () => Promise<void>): void {
    refetchAction.value = action;
  }

  async function fetchItems(companyId: number): Promise<void> {
    const request = ++latestRequest;
    isLoading.value = true;
    currentCompanyId.value = companyId;
    refetchAction.value = null;
    sourceHasMultipleCompanies.value = false;
    try {
      const items = await requestItems(companyId);
      if (request === latestRequest) setAllItems(items);
    } catch (error) {
      if (request === latestRequest) setAllItems([]);
      throw error;
    } finally {
      if (request === latestRequest) isLoading.value = false;
    }
  }

  async function refetchItems(): Promise<void> {
    if (refetchAction.value !== null) {
      await refetchAction.value();
      return;
    }

    if (currentCompanyId.value === null) {
      return;
    }
    await fetchItems(currentCompanyId.value);
  }

  /**
   * Refetches what the store holds after a change elsewhere, e.g. a device group rename that the
   * rows carry. Unlike {@link refetchItems} a failure keeps the current items. A registered loader
   * runs as is, handling its own errors. A load already in flight may predate the change, so its
   * answer is dropped and this refresh ends its loading state instead.
   *
   * @throws The request's error; the current items stay as they were.
   */
  async function refreshItems(): Promise<void> {
    if (refetchAction.value !== null) {
      await refetchAction.value();
      return;
    }
    if (currentCompanyId.value === null) {
      return;
    }
    const request = ++latestRequest;
    try {
      const items = await requestItems(currentCompanyId.value);
      if (request === latestRequest) setAllItems(items);
    } finally {
      if (request === latestRequest) isLoading.value = false;
    }
  }

  function setSourceHasMultipleCompanies(
    items: FlattenedPortfolioItem[]
  ): void {
    if (items.length < 2) {
      sourceHasMultipleCompanies.value = false;
      return;
    }
    const firstId = items[0].company_id;
    sourceHasMultipleCompanies.value = items.some(
      (i) => i.company_id !== firstId
    );
  }

  async function requestItems(
    companyId: number
  ): Promise<FlattenedPortfolioItem[]> {
    const response = await goSolidApi.get(
      `/api/companies/${companyId}/flattened_portfolio_items`
    );
    return response.data.portfolio_items ?? [];
  }

  return {
    allItems,
    isLoading,
    sourceHasMultipleCompanies,
    itemsMap,
    availableItems,
    totalAvailableGOs,
    fetchItems,
    refetchItems,
    refreshItems,
    setAllItems,
    registerRefetch,
    setSourceHasMultipleCompanies,
  };
});
